/**
 * The dispatch side of the calendar — `/v1/admin/availability/*`, `/v1/admin/blackouts*`.
 *
 * **The conflicts queue is computed by the server on every read.** There is no "resolve" endpoint
 * and this module offers no mutation that pretends there is: a conflict disappears when the thing
 * causing it changes — the pandit reopens the time, ops lifts the blackout, or the booking moves.
 * A console button that marked one "handled" would leave the collision in place and hide it.
 *
 * **`truncated` is not decoration.** The scan is bounded, so "50 of 50" and "50 of at least 500"
 * are different facts and the board has to be able to say which it has.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type ConflictRow = components['schemas']['ConflictRow'];
export type BlackoutRow = components['schemas']['BlackoutRow'];
export type SlotRow = components['schemas']['SlotRow'];
type ConflictList = components['schemas']['ConflictList'];
type BlackoutList = components['schemas']['BlackoutList'];
type BlackoutCreated = components['schemas']['BlackoutCreated'];

export interface ConflictQuery extends Record<string, unknown> {
  page: number;
  page_size: number;
}

export function useConflicts(params: ConflictQuery) {
  return useQuery({
    queryKey: keys.availability.conflicts(params),
    queryFn: () =>
      authedClient.request<ConflictList>(
        `/v1/admin/availability/conflicts?page=${params.page}&page_size=${params.page_size}`,
      ),
  });
}

export interface BlackoutQuery extends Record<string, unknown> {
  from: string;
  to: string;
}

export function useBlackouts(params: BlackoutQuery) {
  return useQuery({
    queryKey: keys.availability.blackouts(params),
    queryFn: () =>
      authedClient.request<BlackoutList>(
        `/v1/admin/blackouts?from=${params.from}&to=${params.to}`,
      ),
  });
}

/**
 * Every dispatch write invalidates every dispatch read.
 *
 * Closing a date changes the blackout list and the conflicts queue at once — a hand-maintained
 * map of which keys each write touches goes stale the first time somebody adds a field.
 */
function useDispatchMutation<TArgs, TData>(fn: (args: TArgs) => Promise<TData>) {
  const client = useQueryClient();
  return useMutation<TData, unknown, TArgs>({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.availability.all() }),
  });
}

export function useCreateBlackout() {
  return useDispatchMutation((body: components['schemas']['BlackoutIn']) =>
    authedClient.request<BlackoutCreated>('/v1/admin/blackouts', { method: 'POST', body }),
  );
}

export function useDeleteBlackout() {
  return useDispatchMutation((blackoutId: number) =>
    authedClient.request<void>(`/v1/admin/blackouts/${blackoutId}`, { method: 'DELETE' }),
  );
}

export function useCreateManualSlot() {
  return useDispatchMutation(
    ({ profileId, startAt }: { profileId: number; startAt: string }) =>
      authedClient.request<SlotRow>(`/v1/admin/pandits/${profileId}/slots`, {
        method: 'POST',
        body: { start_at: startAt },
      }),
  );
}
